/**
 * Face alignment maths.
 *
 * A translucent ghost overlay only helps the user aim; it cannot correct the
 * frame they actually took. Without a real transform every timelapse inherits
 * the few pixels of drift between one day's pose and the next, which reads as
 * shake. So the eyes are mapped onto fixed positions and the resulting matrix
 * is both applied at capture and stored, so frames can be re-rendered later.
 *
 * Deliberately a *similarity* transform — rotate, uniform scale, translate. A
 * full affine could also shear and stretch the face to match, which would
 * flatter the alignment while quietly distorting the thing being measured.
 */

export interface Point {
  x: number;
  y: number;
}

/** Canvas `setTransform` argument order: x' = a·x + c·y + e, y' = b·x + d·y + f. */
export type Matrix = [a: number, b: number, c: number, d: number, e: number, f: number];

export interface AlignTransform {
  matrix: Matrix;
  width: number;
  height: number;
}

/**
 * Where the eyes end up, as fractions of the output frame.
 *
 * Slightly above the middle and inset from the edges, so a face fills the frame
 * without the crown of the head being cut off.
 */
export const CANONICAL_LEFT_EYE: Point = { x: 0.355, y: 0.42 };
export const CANONICAL_RIGHT_EYE: Point = { x: 0.645, y: 0.42 };

/**
 * Landmark indices ringing each eye in MediaPipe's 468-point mesh: outer
 * corner, inner corner, upper lid, lower lid. Averaged rather than using the
 * iris centre, which only exists when refinement is enabled.
 */
const EYE_A = [33, 133, 159, 145];
const EYE_B = [362, 263, 386, 374];

function centroid(landmarks: readonly Point[], indices: readonly number[]): Point | null {
  let x = 0;
  let y = 0;

  for (const index of indices) {
    const point = landmarks[index];

    if (!point) {
      return null;
    }

    x += point.x;
    y += point.y;
  }

  return { x: x / indices.length, y: y / indices.length };
}

/**
 * Eye centres in normalised (0..1) coordinates, ordered as they appear in the
 * image rather than by anatomy: MediaPipe's "left" is the subject's left, which
 * lands on the right of an unmirrored frame. Sorting by x keeps the transform
 * correct whichever way the frame is facing.
 */
export function eyeCentres(
  landmarks: readonly Point[],
): { left: Point; right: Point } | null {
  const a = centroid(landmarks, EYE_A);
  const b = centroid(landmarks, EYE_B);

  if (!a || !b) {
    return null;
  }

  return a.x <= b.x ? { left: a, right: b } : { left: b, right: a };
}

/**
 * Maps the given eye positions onto the canonical ones.
 *
 * `left` and `right` are normalised; the matrix produced works in the pixel
 * space of a `width`x`height` source drawn into a `width`x`height` output.
 */
export function similarityTransform(
  left: Point,
  right: Point,
  width: number,
  height: number,
): AlignTransform {
  const source = {
    x: (right.x - left.x) * width,
    y: (right.y - left.y) * height,
  };

  const target = {
    x: (CANONICAL_RIGHT_EYE.x - CANONICAL_LEFT_EYE.x) * width,
    y: (CANONICAL_RIGHT_EYE.y - CANONICAL_LEFT_EYE.y) * height,
  };

  const sourceSpan = Math.hypot(source.x, source.y);

  if (sourceSpan < 1) {
    // Degenerate: both eyes on the same spot means the detection is unusable,
    // and dividing by it would produce an infinite scale.
    return { matrix: [1, 0, 0, 1, 0, 0], width, height };
  }

  const scale = Math.hypot(target.x, target.y) / sourceSpan;
  const angle = Math.atan2(target.y, target.x) - Math.atan2(source.y, source.x);

  const a = scale * Math.cos(angle);
  const b = scale * Math.sin(angle);

  const sourceMid = {
    x: ((left.x + right.x) / 2) * width,
    y: ((left.y + right.y) / 2) * height,
  };

  const targetMid = {
    x: ((CANONICAL_LEFT_EYE.x + CANONICAL_RIGHT_EYE.x) / 2) * width,
    y: ((CANONICAL_LEFT_EYE.y + CANONICAL_RIGHT_EYE.y) / 2) * height,
  };

  return {
    matrix: [
      a,
      b,
      -b,
      a,
      targetMid.x - (a * sourceMid.x - b * sourceMid.y),
      targetMid.y - (b * sourceMid.x + a * sourceMid.y),
    ],
    width,
    height,
  };
}

/** Applies a matrix to a point, for tests and for moving stored landmarks. */
export function applyMatrix([a, b, c, d, e, f]: Matrix, point: Point): Point {
  return {
    x: a * point.x + c * point.y + e,
    y: b * point.x + d * point.y + f,
  };
}

/**
 * How plausible the detected geometry is, 0..1.
 *
 * Not a probability — MediaPipe does not hand one back per face. It penalises
 * the two things that actually produce bad frames: a face too far away for the
 * eyes to be resolved, and a head so tilted that the correction would crop
 * heavily. Stored so a later pass can drop weak frames from a timelapse rather
 * than having to re-run detection over the whole history.
 */
export function alignmentScore(left: Point, right: Point): number {
  const span = Math.hypot(right.x - left.x, right.y - left.y);

  // Eyes closer than 8% of the frame apart mean the subject is far away, and
  // beyond 45% they are close enough that the head is likely cropped.
  const spanScore = clamp01((span - 0.08) / 0.12) * clamp01((0.45 - span) / 0.12);

  const roll = Math.abs(Math.atan2(right.y - left.y, right.x - left.x));
  const rollScore = clamp01(1 - roll / (Math.PI / 6));

  return Number((spanScore * rollScore).toFixed(3));
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
